import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { supabase } from '../services/supabaseClient';
import { Loader2 } from 'lucide-react';

export const AuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const { reloadSession, isLoggedIn } = useSession();
    const [status, setStatus] = useState('Processing login...');

    useEffect(() => {
        const handleCallback = async () => {
            // 1. Handle Hash (Supabase puts tokens in hash)
            const hash = window.location.hash;
            console.log("🔗 AuthCallback processing hash:", hash);

            if (!hash || !hash.includes('access_token')) {
                // If no token in hash, check if we already have a session
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                    console.log("✅ Session found, reloading...");
                    setStatus('Session found, redirecting...');
                    await reloadSession();

                    // Small delay for state sync
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Check if onboarding is needed before redirecting
                    const needsOnboarding = localStorage.getItem('hh_needs_onboarding') === 'true';
                    if (needsOnboarding) {
                        console.log("⚠️ User needs onboarding, staying on role selection");
                        localStorage.removeItem('hh_needs_onboarding');
                        navigate('/');
                        return;
                    }

                    // Navigate to correct dashboard based on role
                    const storedRole = localStorage.getItem('hh_role');
                    if (storedRole === 'WORKER') {
                        navigate('/worker');
                    } else {
                        navigate('/user');
                    }
                    return;
                }

                // No session, no token -> Invalid
                console.error("❌ No access token found in URL");
                setStatus('Login failed. No token found.');
                setTimeout(() => navigate('/'), 2000);
                return;
            }

            // 2. Allow Supabase to detect the hash (it does this automatically on init, but we wait a bit)
            try {
                // Determine user intent from local storage
                const pendingRole = localStorage.getItem('hh_pending_role');
                console.log("🔍 Pending Role detected:", pendingRole);

                // Wait for the session to be established
                const { data: { session }, error } = await supabase.auth.getSession();

                if (error) throw error;

                if (session) {
                    console.log("✅ OAuth Success! User:", session.user.email);
                    setStatus('Setting up your profile...');

                    // Reload session to create/update profile
                    await reloadSession();

                    // Small delay to ensure state is set
                    await new Promise(resolve => setTimeout(resolve, 800));

                    // Check if user needs onboarding (missing phone or incomplete profile)
                    const needsOnboarding = localStorage.getItem('hh_needs_onboarding') === 'true';
                    const finalRole = localStorage.getItem('hh_role') || pendingRole;

                    console.log("📍 Navigation Decision:", { needsOnboarding, finalRole });

                    if (needsOnboarding) {
                        // For new Google users who need to complete profile
                        console.log("🔄 Redirecting to role selection for onboarding");
                        localStorage.removeItem('hh_needs_onboarding');
                        navigate('/');
                        return;
                    }

                    // Navigate directly to the correct dashboard based on role
                    console.log("📍 Navigating to dashboard for role:", finalRole);
                    if (finalRole === 'WORKER') {
                        navigate('/worker');
                    } else {
                        navigate('/user');
                    }
                } else {
                    // Sometimes Supabase needs a moment to parse the hash
                    console.log("⏳ Session not ready, listening for auth state change...");
                    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
                        if (event === 'SIGNED_IN' && session) {
                            console.log("✅ Signed In via Listener!");
                            await reloadSession();

                            // Small delay for state sync
                            await new Promise(resolve => setTimeout(resolve, 800));

                            // Check onboarding status
                            const needsOnboarding = localStorage.getItem('hh_needs_onboarding') === 'true';
                            const finalRole = localStorage.getItem('hh_role') || pendingRole;

                            if (needsOnboarding) {
                                console.log("🔄 Redirecting to role selection for onboarding");
                                localStorage.removeItem('hh_needs_onboarding');
                                navigate('/');
                                return;
                            }

                            // Navigate to correct dashboard
                            if (finalRole === 'WORKER') {
                                navigate('/worker');
                            } else {
                                navigate('/user');
                            }
                        }
                    });

                    // Fallback timeout
                    setTimeout(async () => {
                        await reloadSession();

                        // Small delay for state sync
                        await new Promise(resolve => setTimeout(resolve, 800));

                        const needsOnboarding = localStorage.getItem('hh_needs_onboarding') === 'true';
                        const finalRole = localStorage.getItem('hh_role') || pendingRole;

                        if (needsOnboarding) {
                            console.log("🔄 Redirecting to role selection for onboarding");
                            localStorage.removeItem('hh_needs_onboarding');
                            navigate('/');
                            return;
                        }

                        if (finalRole === 'WORKER') {
                            navigate('/worker');
                        } else {
                            navigate('/user');
                        }
                    }, 3000);
                }

            } catch (e: any) {
                console.error("❌ Auth Callback Error:", e);
                setStatus(`Error: ${e.message}`);
                setTimeout(() => navigate('/'), 3000);
            }
        };

        handleCallback();
    }, [navigate, reloadSession]);

    return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Authenticating</h2>
            <p className="text-slate-500 text-sm font-medium">{status}</p>
        </div>
    );
};
